import { AxiosError } from 'axios';
import { useMutation } from 'react-query';
import { useNavigate } from 'react-router-dom';

import { AppRoutes, routes } from 'router/models';

import { useCorporateContext } from '../CorporateContext';
import {
  applyOfferCode,
  getUserInfo,
  getUserOfferCodes,
  getUserProfile,
  postSignIn,
  replaceOfferCode,
} from '../services';

const handleNewOffer = async (token: string, codeValue: string) => {
  const [userInfo, userProfile] = await Promise.all([
    getUserInfo(token),
    getUserProfile(token),
  ]);

  if (!userInfo || !userProfile) {
    throw new Error('User not found');
  }

  const offerCodes = await getUserOfferCodes(userInfo.id, token);

  // 1. user does not have applied offer code, so we apply a new one
  // 2. user has a different offer code, so we replace it
  // 3. user has the same offer code, so we do nothing
  if (!offerCodes.userCorporateOffer) {
    await applyOfferCode(userInfo.id, codeValue, token);
  } else if (offerCodes.userCorporateOffer.code !== codeValue) {
    await replaceOfferCode(userInfo.id, codeValue, token);
  }

  return { userName: userProfile.firstName };
};

export const useSignInAndApplyOffer = (account: string) => {
  const { codeValue, setUserName, onChatSuccess } = useCorporateContext();
  const navigate = useNavigate();

  return useMutation<
    { userName: string },
    AxiosError,
    { email: string; password: string }
  >({
    mutationFn: async ({ email, password }) => {
      const { succeeded, token } = await postSignIn(email, password);

      if (!succeeded) {
        throw new Error('Sign in failed');
      }

      return handleNewOffer(token, codeValue);
    },
    onSuccess: ({ userName }) => {
      setUserName(userName);
      onChatSuccess();
      navigate(routes[AppRoutes.CorporateSuccess](account));
    },
  });
};

export const useSocialSignInAndApplyOffer = (account: string) => {
  const { codeValue, setUserName, onChatSuccess } = useCorporateContext();
  const navigate = useNavigate();

  return useMutation<{ userName: string }, AxiosError, { token: string }>({
    mutationFn: async ({ token }) => handleNewOffer(token, codeValue),
    onSuccess: ({ userName }) => {
      setUserName(userName);
      onChatSuccess();
      navigate(routes[AppRoutes.CorporateSuccess](account));
    },
  });
};
